import { Controller } from '@hotwired/stimulus';
import { makeZip } from 'client-zip';
import { saveBlob } from '../save_blob';
import { b64decode } from '../vault_crypto';

const EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/svg+xml': 'svg',
  'application/pdf': 'pdf',
};

// Row-level windowed image table for the claim "Show images" wall.
//
// The server embeds every row as JSON (no row markup). Only the rows in or near the
// viewport are rendered, between two spacer rows that keep the full scroll height, so a
// 1000-device claim stays light. Rendered rows start as skeletons.
//
// Loading (fetch blob + vault decrypt, same as autodecrypt) waits until scrolling has
// settled and then covers the visible rows first, then a small overscan. Rows leaving
// the window are dropped and their in-flight fetches aborted, so jumping to the end never
// loads everything in between and stale responses never land in the table.
//
// With the download privilege a checkbox column is added. "Download selected" asks the
// server for the archive manifest (it enforces the privilege, names the files and audits),
// then decrypts one image at a time in the vault and streams it into a ZIP that is piped
// straight to disk — the vault key, and so the plaintext, never leave the browser.
export default class extends Controller {
  static targets = [
    'body',
    'vaultNotice',
    'selectAll',
    'downloadButton',
    'progress',
    'progressFill',
    'progressCount',
    'progressPercent',
    'status',
  ];
  static values = {
    payload: Object,
    href: String,
    missing: String,
    loadFailed: String,
    overscan: { type: Number, default: 2 },
    settle: { type: Number, default: 150 },
    downloadUrl: String,
    downloadFilename: String,
    i18n: Object,
  };

  connect() {
    if (!this.hasBodyTarget) return;

    ({ columns: this.columns, rows: this.rows } = this.payloadValue);
    this.selectable = this.hasDownloadUrlValue;
    this.offset = this.selectable ? 1 : 0; // the checkbox column precedes the image columns
    this.selected = new Set(); // device claim ids, kept across windowing
    this.rowHeight = 192; // estimate, corrected from the first two rendered rows
    this.rendered = new Map(); // row index -> { els: [headTr, imgTr], ctrl, loaded }
    this.topSpacer = this.spacer();
    this.bottomSpacer = this.spacer();
    this.bodyTarget.replaceChildren(this.topSpacer, this.bottomSpacer);

    this.onScroll = () => {
      if (!this.frame) {
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.loadVisible(), this.settleValue);
    };
    this.onVaultOpened = () => this.loadVisible();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.element.addEventListener('scroll', this.onScroll, { passive: true }); // fullscreen container
    window.addEventListener('vault:vault-opened', this.onVaultOpened);
    window.addEventListener('vault-opened', this.onVaultOpened);
    this.refresh();
    this.updateSelection();
  }

  disconnect() {
    if (!this.onScroll) return;

    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.element.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('vault:vault-opened', this.onVaultOpened);
    window.removeEventListener('vault-opened', this.onVaultOpened);
    cancelAnimationFrame(this.frame);
    clearTimeout(this.settleTimer);
    this.rendered.forEach((row) => row.ctrl.abort());
  }

  // Re-window now and load once settled (also wired to the fullscreen toggle).
  refresh() {
    this.onScroll?.();
  }

  visibleRange() {
    const origin = this.topSpacer.getBoundingClientRect().top; // top of row 0
    const first = Math.floor(-origin / this.rowHeight);
    const last = Math.floor((window.innerHeight - origin) / this.rowHeight);
    return [Math.max(0, first), Math.min(this.rows.length - 1, last)];
  }

  update() {
    const [first, last] = this.visibleRange();
    const start = Math.max(0, first - this.overscanValue);
    const end = Math.min(this.rows.length - 1, last + this.overscanValue);

    for (const [i, row] of this.rendered) {
      if (i < start || i > end) {
        row.ctrl.abort();
        row.els.forEach((el) => el.remove());
        this.rendered.delete(i);
      }
    }

    // insert missing rows in order without moving kept ones (moving would remount
    // their image controllers and duplicate the controls)
    let next = this.bottomSpacer;
    for (let i = end; i >= start; i--) {
      let row = this.rendered.get(i);
      if (!row) {
        row = this.buildRow(this.rows[i]);
        this.rendered.set(i, row);
        next.before(...row.els);
      }
      next = row.els[0];
    }

    this.topSpacer.firstChild.style.height = `${start * this.rowHeight}px`;
    this.bottomSpacer.firstChild.style.height = `${Math.max(0, this.rows.length - 1 - end) * this.rowHeight}px`;
    this.measure();
  }

  // One-time correction of the row height from two consecutive rendered rows.
  measure() {
    if (this.measured || this.rendered.size < 2) return;

    const [a] = [...this.rendered.keys()].sort((x, y) => x - y);
    const h =
      this.rendered.get(a + 1).els[0].getBoundingClientRect().top -
      this.rendered.get(a).els[0].getBoundingClientRect().top;
    this.measured = true;
    if (h > 0 && Math.abs(h - this.rowHeight) > 0.5) {
      this.rowHeight = h;
      this.update();
    }
  }

  loadVisible() {
    if (!this.vaultOpen()) {
      this.toggleVaultNotice(true);
      return;
    }
    this.toggleVaultNotice(false);

    const [first, last] = this.visibleRange();
    const order = [];
    for (let i = first; i <= last; i++) order.push(i); // visible rows first
    for (let k = 1; k <= this.overscanValue; k++) order.push(last + k, first - k); // then overscan
    order.forEach((i) => {
      const row = this.rendered.get(i);
      if (row && !row.loaded) this.loadRow(row, this.rows[i]);
    });
  }

  loadRow(row, data) {
    row.loaded = true;
    const tds = row.els[1].cells;
    data.cells.forEach((cell, c) => cell && this.loadCell(row, cell, tds[c + this.offset]));
  }

  async loadCell(row, cell, td) {
    const { signal } = row.ctrl;
    try {
      if (cell.caption_enc) {
        const plain = await window.vault.decryptMessage(cell.caption.trim());
        if (!signal.aborted && plain !== null)
          td.querySelector('.image-wall__caption').textContent = plain;
      }
      const res = await fetch(cell.enc, { signal });
      // a refused blob redirects (flash) instead of erroring
      if (!res.ok || res.redirected) throw new Error(`HTTP ${res.status}`);
      const src = await window.vault.decryptMessage(await res.text());
      if (signal.aborted) return; // row left the window meanwhile: drop the stale result
      this.inject(td, src);
    } catch (e) {
      if (signal.aborted) return;
      console.error('image-wall: could not load', e);
      this.showFailed(row, cell, td);
    }
  }

  // Stop the loading pulse and let the user retry just this image.
  showFailed(row, cell, td) {
    const ph = td.querySelector('.image-wall__ph');
    ph.classList.remove('animate-pulse');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'text-primary hover:underline p-2';
    retry.textContent = this.loadFailedValue;
    retry.addEventListener('click', () => {
      ph.classList.add('animate-pulse');
      retry.remove();
      this.loadCell(row, cell, td);
    });
    ph.replaceChildren(retry);
  }

  buildRow(data) {
    const head = document.createElement('tr');
    head.className = 'image-wall__rowhead';
    const headCell = head.insertCell();
    headCell.colSpan = this.columns.length + this.offset;
    const link = document.createElement('a');
    link.href = this.hrefValue.replace('__UID__', encodeURIComponent(data.uid));
    link.className = 'text-xs text-primary hover:underline break-all';
    link.textContent = data.serial;
    headCell.append(link);

    const imgs = document.createElement('tr');
    imgs.className = 'image-wall__imgrow';
    let checkbox = null;
    if (this.selectable) {
      const td = imgs.insertCell();
      td.className = 'image-wall__select';
      checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = this.selected.has(data.id);
      checkbox.setAttribute('aria-label', `${this.i18nValue.selectRow}: ${data.serial}`);
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) this.selected.add(data.id);
        else this.selected.delete(data.id);
        this.updateSelection();
      });
      td.append(checkbox);
    }
    data.cells.forEach((cell) => {
      const td = imgs.insertCell();
      if (!cell) {
        const missing = document.createElement('div');
        missing.className =
          'image-wall__missing rounded flex items-center justify-center text-grey-3';
        missing.title = this.missingValue;
        missing.textContent = '—';
        td.append(missing);
        return;
      }
      const ph = document.createElement('div');
      ph.className =
        'image-wall__ph bg-lighterGray animate-pulse rounded flex items-center justify-center text-xs text-grayText';
      td.append(ph);
      if (cell.caption) {
        const caption = document.createElement('div');
        caption.className = 'image-wall__caption text-xs text-center mt-1 break-all';
        if (!cell.caption_enc) caption.textContent = cell.caption;
        td.append(caption);
      }
    });

    return { id: data.id, checkbox, els: [head, imgs], ctrl: new AbortController(), loaded: false };
  }

  inject(td, src) {
    const ph = td.querySelector('.image-wall__ph');
    if (src === null) {
      ph.classList.remove('animate-pulse');
      ph.textContent = 'NO DATA';
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'image-wall__img';
    if (src.startsWith('data:application/pdf;')) {
      const a = document.createElement('a');
      a.href = src;
      a.target = '_blank';
      a.className = 'text-primary hover:underline text-sm';
      a.innerText = 'PDF';
      wrapper.append(a);
    } else {
      const img = document.createElement('img');
      img.src = src;
      img.style.maxWidth = '120px';
      img.style.maxHeight = '120px';
      wrapper.append(img);
      // Mount the shared image controller so the usual rotate/enlarge/fullscreen
      // controls appear — same mechanism autodecrypt uses for encrypted images.
      wrapper.dataset.controller = 'image';
    }
    ph.replaceWith(wrapper);
  }

  spacer() {
    const tr = document.createElement('tr');
    const td = tr.insertCell();
    td.colSpan = this.columns.length + this.offset;
    td.style.padding = '0';
    td.style.height = '0';
    return tr;
  }

  toggleAll(event) {
    if (event.target.checked) this.rows.forEach((row) => this.selected.add(row.id));
    else this.selected.clear();
    this.rendered.forEach((row) => (row.checkbox.checked = this.selected.has(row.id)));
    this.updateSelection();
  }

  updateSelection() {
    if (!this.selectable) return;

    const n = this.selected.size;
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = n > 0 && n === this.rows.length;
      this.selectAllTarget.indeterminate = n > 0 && n < this.rows.length;
    }
    this.downloadButtonTarget.disabled = n === 0 || this.downloading;
    const label = this.downloadButtonTarget.getAttribute('aria-label');
    this.downloadButtonTarget.title = n ? `${label} (${n})` : label;
  }

  async download() {
    if (this.downloading || this.selected.size === 0) return;
    if (!this.vaultOpen()) {
      // openModal re-runs the callback once the vault is unlocked, so one click is enough
      window.vault?.openModal?.(() => this.download());
      return;
    }

    // Pick the target file first: the picker needs this click's user activation, which
    // the awaits below would use up. Without it (Firefox/Safari, or after unlocking the
    // vault) the ZIP is collected into a Blob instead of being streamed to disk.
    let sink = null;
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: this.downloadFilenameValue,
          types: [{ description: 'ZIP', accept: { 'application/zip': ['.zip'] } }],
        });
        sink = await handle.createWritable();
      } catch (e) {
        if (e.name === 'AbortError') return; // the user cancelled the dialog
      }
    }

    this.setDownloading(true);
    try {
      const response = await fetch(this.downloadUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ ids: [...this.selected] }),
      });
      if (!response.ok) throw new Error(`manifest request failed: ${response.status}`);

      const { entries } = await response.json();
      const stats = { done: 0, skipped: 0, total: entries.length };
      this.showProgress(stats);
      const zip = makeZip(this.decryptedFiles(entries, stats));
      if (sink) await zip.pipeTo(sink);
      else saveBlob(await new Response(zip).blob(), this.downloadFilenameValue);

      this.statusTarget.textContent = this.fmt(this.i18nValue.done, {
        count: stats.done - stats.skipped,
      });
      if (stats.skipped)
        window.showMessage?.(this.fmt(this.i18nValue.skipped, { count: stats.skipped }));
    } catch (e) {
      console.error('image-wall: download failed', e);
      await sink?.abort().catch(() => {}); // discard the partial file
      this.statusTarget.textContent = '';
      window.showMessage?.(this.i18nValue.failed);
    } finally {
      this.setDownloading(false);
    }
  }

  // One image at a time: the ZIP stream pulls the next file only once the previous one is
  // written, so at most one decrypted image is held in memory.
  async *decryptedFiles(entries, stats) {
    for (const entry of entries) {
      try {
        const response = await fetch(entry.url);
        const file = response.ok
          ? this.toFile(
              entry.path,
              await window.vault.decryptMessage((await response.text()).trim())
            )
          : null;
        if (file) yield file;
        else stats.skipped++;
      } catch (e) {
        console.error('image-wall: could not decrypt', entry.path, e);
        stats.skipped++;
      }
      stats.done++;
      this.showProgress(stats);
    }
  }

  // decrypted images are data URIs; the MIME type gives the file extension
  toFile(path, dataUri) {
    const match = dataUri?.match(/^data:([^;,]+)[^,]*;base64,(.*)$/s);
    if (!match) return null;

    const [, mime, base64] = match;
    const ext =
      EXTENSIONS[mime] ||
      mime
        .split('/')
        .pop()
        .replace(/[^a-z0-9]/gi, '') ||
      'bin';
    return { name: `${path}.${ext}`, input: b64decode(base64), lastModified: new Date() };
  }

  // Job-tracking style pill: the count rides inside the teal fill, the percent sits right;
  // the full sentence goes to aria-valuetext for screen readers.
  showProgress({ done, total }) {
    const percent = total ? Math.round((done / total) * 100) : 0;
    this.progressTarget.setAttribute('aria-valuemax', total || 1);
    this.progressTarget.setAttribute('aria-valuenow', done);
    this.progressTarget.setAttribute(
      'aria-valuetext',
      this.fmt(this.i18nValue.progress, { done, total })
    );
    this.progressFillTarget.style.width = `${percent}%`;
    this.progressCountTarget.textContent = `${done}/${total}`;
    this.progressPercentTarget.textContent = `${percent}%`;
    this.statusTarget.textContent = '';
  }

  setDownloading(on) {
    this.downloading = on;
    this.progressTarget.classList.toggle('hidden', !on);
    this.updateSelection();
  }

  fmt(template, vars) {
    return template.replace(/%\{(\w+)\}/g, (_, key) => vars[key]);
  }

  vaultOpen() {
    return !!(window.vault && window.vault.is_open());
  }

  toggleVaultNotice(show) {
    if (this.hasVaultNoticeTarget) this.vaultNoticeTarget.classList.toggle('hidden', !show);
  }
}
